import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { colors } from "sexy";
import { useState, useEffect } from "react";
import { Project, ProjectPhoto, User } from "types";
import ConfirmationModal from "@components/support/ConfirmationModal";
import { CustomImage } from "@components/ui/images/CustomImage";
import { showNotification } from "context/notification.service";

const getVisiblePhotosFromProject = (project: Project, userData?: User) => {
  if (!project?.photos) return [];

  // If user is guest, filter out hidden photos
  // For non-guests, show all photos
  return project.photos.filter((photo) => !photo.hidden || userData);
};

interface PhotoModalProps {
  onClose: () => void;
  photo_id?: string;
  activeProject?: Project;
  setActiveProject?: (project: Project) => void;
  userData?: User;
}

export const PhotoModal = ({
  onClose,
  photo_id,
  activeProject,
  setActiveProject,
  userData,
}: PhotoModalProps) => {
  const [photo, setPhoto] = useState<ProjectPhoto | undefined>(
    activeProject?.photos.find((p) => p.id === photo_id),
  );
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  function handleDelete() {
    if (!activeProject || !photo || !setActiveProject) return;

    setActiveProject({
      ...activeProject,
      photos: activeProject.photos.filter((p) => p.id !== photo.id),
    });
    onClose();
  }

  function handleToggleHidden() {
    if (!activeProject || !photo || !setActiveProject) return;

    const updatedPhoto = { ...photo, hidden: !photo.hidden };
    setPhoto(updatedPhoto);
    setActiveProject({
      ...activeProject,
      photos: activeProject.photos.map((p) =>
        p.id === photo.id ? updatedPhoto : p,
      ),
    });
  }

  function goToNextPhoto() {
    if (!activeProject || !photo) return;

    const photos = getVisiblePhotosFromProject(activeProject, userData);
    const currentIndex = photos.findIndex((p) => p.id === photo.id);
    const nextIndex = (currentIndex + 1) % photos.length;
    const nextPhoto = photos[nextIndex];
    setPhoto(nextPhoto);
  }

  const isFacade = photo?.file.id === activeProject?.infos.general.photo_file?.id;

  function handleSetFacade() {
    if (!activeProject || !photo || !setActiveProject) return;

    setActiveProject({
      ...activeProject,
      infos: {
        ...activeProject.infos,
        general: {
          ...activeProject.infos.general,
          photo_file: photo.file,
        },
      },
    });
    showNotification({
      type: "success",
      message: "Photo de façade définie",
    });
  }

  function goToPreviousPhoto() {
    if (!activeProject || !photo) return;

    const photos = getVisiblePhotosFromProject(activeProject, userData);
    const currentIndex = photos.findIndex((p) => p.id === photo.id);
    const previousIndex = (currentIndex - 1 + photos.length) % photos.length;
    const previousPhoto = photos[previousIndex];
    setPhoto(previousPhoto);
  }

  useEffect(() => {
    if (photo_id && activeProject) {
      const foundPhoto = activeProject.photos.find((p) => p.id === photo_id);
      setPhoto(foundPhoto);
    }
  }, [photo_id, activeProject]);

  if (!photo || !activeProject) {
    return null;
  }

  return (
    <BaseModal
      width="max-w-800"
      title="Photo"
      startIcon={<Icon1 name="image" size={20} />}
      onClose={onClose}
    >
      {confirmationOpen && (
        <ConfirmationModal
          onClose={() => setConfirmationOpen(false)}
          onConfirm={handleDelete}
          title="Supprimer la photo"
          message="Êtes-vous sûr de vouloir supprimer cette photo ?"
        />
      )}

      <div className="bg-white flex flex-col gap-4 p-4">
        {/* Photo Display */}
        <div
          className="relative w-full rounded-3xl overflow-hidden"
          style={{ height: "400px" }}
        >
          <CustomImage
            source={{ uri: photo.file.url }}
            alt="Photo"
            className="w-full h-full"
            hidden={photo.hidden}
          />
        </div>

        {/* Navigation Controls */}
        <div className="flex flex-row justify-center gap-2.5 items-center">
          <button
            onClick={goToPreviousPhoto}
            className="w-12 h-12 rounded-full bg-neutral-800 hover:bg-neutral-700 transition-colors flex items-center justify-center"
            title="Photo précédente"
          >
            <Icon1 name="left" size={20} color="white" />
          </button>

          <button
            onClick={handleToggleHidden}
            className="w-12 h-12 rounded-full bg-neutral-800 hover:bg-neutral-700 transition-colors flex items-center justify-center"
            title={photo.hidden ? "Rendre visible" : "Masquer la photo"}
          >
            <Icon1 name="invisible" size={24} color="white" />
          </button>

          <button
            onClick={handleSetFacade}
            className={`w-12 h-12 rounded-full transition-colors flex items-center justify-center ${
              isFacade
                ? "bg-blue-500 hover:bg-blue-400"
                : "bg-neutral-800 hover:bg-neutral-700"
            }`}
            title="Définir comme photo de façade"
          >
            <Icon1 name="image" size={24} color="white" />
          </button>

          <button
            onClick={() => setConfirmationOpen(true)}
            className="w-12 h-12 rounded-full bg-neutral-800 hover:bg-neutral-700 transition-colors flex items-center justify-center"
            title="Supprimer la photo"
          >
            <Icon1 name="delete" size={24} color="white" />
          </button>

          <button
            onClick={goToNextPhoto}
            className="w-12 h-12 rounded-full bg-neutral-800 hover:bg-neutral-700 transition-colors flex items-center justify-center"
            title="Photo suivante"
          >
            <Icon1 name="right" size={20} color="white" />
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <Button variant="small" label="Fermer" onClick={onClose} />
      </div>
    </BaseModal>
  );
};
